import { useEffect, useRef } from 'react';
import { Size } from '@bms2/shared';
import { Icon } from '../Icon';
import { sizeClassMap } from '../classMaps';
import styles from './choice.module.css';

/**
 * Checkbox (piece 3): the native checkbox with appearance none and the plan's
 * paint. A navy fill with a white check when checked, a white dash when
 * indeterminate, a navy border on hover, the 2px navy ring on focus, 40%
 * opacity when disabled, and the full row as one click target.
 */
export interface CheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  indeterminate?: boolean;
  size?: Size;
  disabled?: boolean;
  name?: string;
  /** The label leaves the screen and stays in the accessibility tree: a selection cell, a grid cell (piece 4). */
  labelHidden?: boolean;
}

export const checkboxDocumentedStates = ['unchecked', 'checked', 'indeterminate', 'disabled', 'small', 'large'] as const;

const sizeClass = sizeClassMap({ small: styles.small!, large: styles.large! });

export function Checkbox({ label, checked, onChange, indeterminate = false, size = Size.Medium, disabled = false, name, labelHidden = false }: CheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  const mark = indeterminate ? 'indeterminate' : checked ? 'confirmed' : null;
  return (
    <label className={styles.row} data-size={size}>
      <span className={`${styles.box} ${sizeClass[size]}`}>
        <input
          ref={ref}
          type="checkbox"
          className={`${styles.native} ${styles.checkbox}`}
          name={name}
          checked={checked}
          disabled={disabled}
          aria-checked={indeterminate ? 'mixed' : checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        {mark ? (
          <span className={styles.mark} data-mark={mark}>
            <Icon meaning={mark} size="inline" />
          </span>
        ) : null}
      </span>
      <span className={labelHidden ? styles.hiddenLabel : undefined}>{label}</span>
    </label>
  );
}
